/**
 * An addressed part of a course view (`?part=`, #1393 contract 4): Grades'
 * attendance for a learner (R1), People's groups for a teacher (R2) and Desk's
 * Resources in Outline (R15). The part's heading takes arrival focus in place
 * of the frame's `h1`; the frame yields when its view row draws the addressed
 * part (`CourseViewRow.parts`), and Desk yields to Resources.
 */
import { useEffect, useRef, type RefObject } from "react";
import { useSearchParams } from "react-router";
import { focusFrameHeading, useVisitOpen } from "./use-frame-focus";

export type CoursePart = "attendance" | "groups" | "resources";

/**
 * The addressed part's visible arrival focus: a full perimeter, as Bento's own
 * heads (P5). `outline-none` sets the outline style to none, so the focused
 * state names its style again; width and colour alone draw nothing.
 */
export const PART_FOCUS = "scroll-mt-4 outline-none focus-visible:outline-solid focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring";

/** How long an arrival keeps its part in view while the content above it settles. */
export const PART_SETTLE_MS = 4000;

/** The one part the address names, or null; a repeated `part` names none. */
export function addressedPart(params: URLSearchParams): string | null {
  const parts = params.getAll("part");
  return parts.length === 1 ? parts[0] : null;
}

/**
 * A ref for the part's heading, focused and scrolled to when the address names
 * the part. Reads above it (the team, private messages) can resolve after the
 * arrival and push it down, so it stays in view until they settle: while it
 * keeps focus, for a bounded time, and never past the person's own scroll,
 * key or press, or a visit opening.
 */
export function usePartHeading<T extends HTMLElement = HTMLHeadingElement>(part: CoursePart) {
  const [params] = useSearchParams();
  const addressed = addressedPart(params) === part;
  const visitOpen = useVisitOpen();
  const heading = useRef<T>(null);
  useEffect(() => {
    const node = heading.current;
    if (!addressed || !node || visitOpen.current) return;
    return holdPart(node, visitOpen);
  }, [addressed, visitOpen]);
  return heading;
}

/**
 * The same arrival focus for a part another owner draws, such as a Bento box
 * whose head the kit renders: `selector` finds it inside `container`.
 */
export function usePartTarget(part: CoursePart, container: RefObject<HTMLElement | null>, selector: string) {
  const [params] = useSearchParams();
  const addressed = addressedPart(params) === part;
  const visitOpen = useVisitOpen();
  useEffect(() => {
    const node = container.current?.querySelector<HTMLElement>(selector);
    if (!addressed || !node || visitOpen.current) return;
    return holdPart(node, visitOpen);
  }, [addressed, visitOpen, container, selector]);
}

function holdPart(node: HTMLElement, visitOpen: RefObject<boolean>): () => void {
  focusFrameHeading(node, visitOpen);
  node.scrollIntoView?.({ block: "start" });
  if (typeof ResizeObserver === "undefined") return () => {};
  let top = node.getBoundingClientRect().top;
  const hold = new ResizeObserver(() => {
    if (document.activeElement !== node || visitOpen.current) return release();
    const now = node.getBoundingClientRect().top;
    if (Math.abs(now - top) < 1) return;
    node.scrollIntoView?.({ block: "start" });
    top = node.getBoundingClientRect().top;
  });
  hold.observe(document.body);
  const timer = window.setTimeout(release, PART_SETTLE_MS);
  const yields = ["wheel", "touchstart", "keydown", "pointerdown"] as const;
  function release() {
    hold.disconnect(); window.clearTimeout(timer);
    yields.forEach(kind => window.removeEventListener(kind, release, true));
  }
  yields.forEach(kind => window.addEventListener(kind, release, { capture: true, passive: true }));
  return release;
}
